/* eslint-disable react/jsx-props-no-spreading */
import React, {
  FC, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, forwardRef,
} from 'react';

const BASE_INPUT_CLASS_NAME = 'w-full px-3.5 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-brand bg-white text-slate-800 transition-colors';

type FormInputProps = InputHTMLAttributes<HTMLInputElement> & { error?: string };

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(({ error, ...props }, ref) => (
  <div>
    <input
      ref={ref}
      {...props}
      className={`${BASE_INPUT_CLASS_NAME} ${error ? 'border-red-400 focus:border-red-400' : ''} ${props.className || ''}`}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
));
FormInput.displayName = 'FormInput';

interface FormSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  error?: string;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(({
  children, placeholder = 'Select...', error, ...props
}, ref) => (
  <div>
    <div className="relative">
      <select
        ref={ref}
        {...props}
        className={`${BASE_INPUT_CLASS_NAME} appearance-none ${error ? 'border-red-400 focus:border-red-400' : ''} ${props.className || ''}`}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400 text-xs">▾</span>
    </div>
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
));
FormSelect.displayName = 'FormSelect';

type FormTextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: string };

export const FormTextArea = forwardRef<HTMLTextAreaElement, FormTextAreaProps>(({ error, ...props }, ref) => (
  <div>
    <textarea
      ref={ref}
      {...props}
      className={`${BASE_INPUT_CLASS_NAME} resize-y ${error ? 'border-red-400 focus:border-red-400' : ''} ${props.className || ''}`}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
));
FormTextArea.displayName = 'FormTextArea';

interface FormLabelProps {
  children: React.ReactNode;
  isRequired?: boolean;
  hint?: string;
}

export const FLabel: FC<FormLabelProps> = ({ children, isRequired, hint }) => (
  <div className="mb-1.5" role="group">
    <span className="text-sm font-semibold text-slate-700 block">
      {children}
      {isRequired && <span className="text-red-400 ml-0.5">*</span>}
    </span>
    {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

interface RadioProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

export const Radio: FC<RadioProps> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 text-sm font-medium ${
      selected ? 'text-brand' : 'text-slate-700'
    }`}
  >
    <span
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        selected ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
      }`}
    >
      {selected && <span className="w-2 h-2 rounded-full bg-white block" />}
    </span>
    {label}
  </button>
);

interface CheckboxProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

export const Checkbox: FC<CheckboxProps> = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start gap-2 text-sm text-slate-700 hover:text-slate-900 text-left w-full"
  >
    <span
      className={`w-4 h-4 mt-0.5 border rounded flex items-center justify-center flex-shrink-0 ${
        selected ? 'border-brand bg-brand' : 'border-slate-300 bg-white'
      }`}
    >
      {selected && <span className="text-white text-xs leading-none">✓</span>}
    </span>
    {label}
  </button>
);

export const SectionDivider: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-3 pt-2 pb-1">
    <div className="h-px flex-1 bg-slate-200" />
    <span className="text-xs font-bold uppercase tracking-widest text-brand">
      {children}
    </span>
    <div className="h-px flex-1 bg-slate-200" />
  </div>
);
